import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { KeywordTrend, ActiveCommenter, TrendingTopic, TopicMention } from "@/types/supabase"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Converts an array of objects to CSV format
 * @param data Array of objects to convert
 * @param headers Optional custom headers (uses object keys if not provided)
 * @returns CSV formatted string
 */
export function convertToCSV<T extends Record<string, any>>(
  data: T[], 
  headers?: string[]
): string {
  if (!data || data.length === 0) return '';
  
  // Use provided headers or extract from the first object
  const csvHeaders = headers || Object.keys(data[0]);
  
  // Create header row
  const headerRow = csvHeaders.join(',');
  
  // Create data rows
  const rows = data.map(item => {
    return csvHeaders.map(header => {
      // Handle values that need quotes (strings with commas, quotes, or newlines)
      const value = item[header] !== undefined ? String(item[header]) : '';
      if (value.includes(',') || value.includes('"') || value.includes('\n')) {
        return `"${value.replace(/"/g, '""')}"`;
      }
      return value;
    }).join(',');
  });
  
  // Combine header and data rows
  return [headerRow, ...rows].join('\n');
}

/**
 * Downloads data as a CSV file
 * @param data The data to download
 * @param filename The name of the file
 * @param headers Optional custom headers
 */
export function downloadCSV<T extends Record<string, any>>(
  data: T[], 
  filename: string,
  headers?: string[]
): void {
  if (!data || data.length === 0) return;
  
  const csv = convertToCSV(data, headers);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
}

/**
 * Prepares keyword trends data for CSV export
 * @param data Keyword trends data
 * @returns Formatted data for CSV export
 */
export function prepareKeywordTrendsForCSV(data: KeywordTrend[] | { text: string; size: number; count: number }[]): Record<string, any>[] {
  return data.map(item => {
    if ('text' in item) {
      return {
        Keyword: item.text,
        Occurrences: item.count
      };
    }
    return {
      Keyword: item.keyword,
      Occurrences: item.occurrence_count
    };
  });
}

/**
 * Prepares active commenters data for CSV export
 * @param data Active commenters data
 * @returns Formatted data for CSV export
 */
export function prepareActiveCommentersForCSV(
  data: (ActiveCommenter | { name: string; comments: number; recentComments: { text: string; created_at: string }[] })[]
): Record<string, any>[] {
  return data.map(item => {
    if ('name' in item) {
      return {
        Commenter: item.name,
        'Comment Count': item.comments,
        'Recent Comments': item.recentComments.map(c => c.text).join(' | ')
      };
    }
    return {
      Commenter: item.author,
      'Comment Count': item.comment_count
    };
  });
}

/**
 * Prepares trending topics data for CSV export
 * @param data Trending topics data
 * @returns Formatted data for CSV export
 */
export function prepareTrendingTopicsForCSV(
  data: (TrendingTopic | { topic: string; mentions: number; details: TopicMention[] })[]
): Record<string, any>[] {
  return data.map(item => {
    if ('details' in item) {
      return {
        Topic: item.topic,
        'Mention Count': item.mentions,
        'Sample Mentions': item.details.map(d => d.text).join(' | ')
      };
    }
    return {
      Topic: item.topic,
      'Mention Count': item.mention_count
    };
  });
}
